import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import AdminNotificationService from '../services/AdminNotificationService';
import { useSession } from '../../contexts/SessionContext';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10002;
  animation: ${props => props.closing ? fadeOut : fadeIn} 0.3s ease-out;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${props => props.closing ? fadeOut : scaleIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-width: 90%;
    max-height: 85vh;
  }
`;

const ModalScrollArea = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ea7317;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #d97706;
  }
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ModalMessage = styled.p`
  margin: 0;
  font-size: 16px;
  color: #4a5568;
  line-height: 1.6;
  white-space: pre-wrap;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ActionButton = styled.button`
  margin-top: 24px;
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #ea7317 0%, #f59e0b 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(234, 115, 23, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 16px;
  background: ${props => {
    switch(props.type) {
      case 'error': return '#fee';
      case 'warning': return '#fff3cd';
      case 'success': return '#d1fae5';
      default: return '#dbeafe';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      default: return '#3b82f6';
    }
  }};
`;

const getIcon = (type, size = 28) => {
  switch(type) {
    case 'error': return <AlertCircle size={size} color="#ef4444" />;
    case 'warning': return <AlertTriangle size={size} color="#f59e0b" />;
    case 'success': return <CheckCircle size={size} color="#10b981" />;
    default: return <Info size={size} color="#3b82f6" />;
  }
};

export default function NotificationModal() {
  const [currentNotification, setCurrentNotification] = useState(null);
  const [closing, setClosing] = useState(false);
  const { user } = useSession();

  useEffect(() => {
    if (!user?.id) {
      return;
    }


    // Subscribe to changes
    const unsubscribe = AdminNotificationService.subscribe((notifs) => {
      // Only show 'popup' type here
      const popupNotifs = notifs.filter(n => n.display_type === 'popup');
      
      if (popupNotifs.length > 0 && !currentNotification) {
        setCurrentNotification(popupNotifs[0]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [user?.id, currentNotification]);

  const handleClose = async () => {
    if (!user?.id || !currentNotification) return;

    setClosing(true);

    setTimeout(async () => {
      await AdminNotificationService.markAsViewed(user.id, currentNotification.id);
      setCurrentNotification(null);
      setClosing(false);
    }, 300);
  };

  if (!currentNotification) return null;

  return (
    <ModalOverlay show={!!currentNotification} closing={closing}>
      <ModalContent closing={closing} onClick={(e) => e.stopPropagation()}>
        <ModalScrollArea>
          <TypeBadge type={currentNotification.type}>
            {getIcon(currentNotification.type, 16)}
            {currentNotification.type || 'info'}
          </TypeBadge>
          <ModalTitle>
            {getIcon(currentNotification.type)}
            {currentNotification.title}
          </ModalTitle>
          <ModalMessage>
            {currentNotification.message}
          </ModalMessage>
          <ActionButton onClick={handleClose}>
            Begrepen
          </ActionButton>
        </ModalScrollArea>
      </ModalContent>
    </ModalOverlay>
  );
}
